import uuid from 'uuid/v4';
import {
  PageView,
  UserActionMessage,
  CustomerUserMessage,
  MessageType,
  SystemMessage,
  UserActionType,
  CollectionName,
  WebsiteConversation,
  Conversation,
  CheckAvailabilityCarouselData,
  CheckAvailabilityCarouselMessage,
  ConversationCheckAvailability,
  ConversationCheckAvailabilityReady,
  GetProductAvailability,
  DocumentObject,
} from '@tomis-tech/types';

import { getPageViewUserAction } from '../../tracking/utils';
import { SITE_SLUG } from '../consts';
import { addBreadcrumb, logError, Category } from '.././sentry-iframe';
import {
  getProductV2,
  getProductAvailabilityV2,
} from '../services/checkAvailability';
import { validateCheckAvailabilityParameters } from '../utils';
import { PARENT_PAGE_VIEW } from '..';

export const getPageViewUserActionMessage = (
  pageView: PageView
): UserActionMessage => {
  const pageViewUserAction = getPageViewUserAction(pageView);
  return {
    type: MessageType.UserAction,
    sent: new Date(),
    /**
     * Do NOT edit text format. Mobile app checks for `//`
     * from http:// or https:// to display Pageviews
     */
    text: `User is now viewing ${pageView.page.url.href}`,
    receivedBy: [],
    seenBy: [],
    userAction: pageViewUserAction,
    userWebsiteContext: { pageView },
  };
};

export const getLinkClickUserActionMessage = (
  link: string,
  pageView: PageView
): UserActionMessage => {
  return {
    type: MessageType.UserAction,
    sent: new Date(),
    /**
     * Do NOT edit text format. Mobile app checks for on `//`, `tel:` or `mailto:`
     * to display Link Clicks
     */

    text: `User clicked: ${link}`,
    receivedBy: [],
    seenBy: [],
    userAction: {
      type: UserActionType.LinkClick,
      timestamp: new Date(),
      data: link,
    },
    userWebsiteContext: { pageView },
  };
};

export const createCustomerUserMessage = ({
  text,
  uid,
}: {
  text: string;
  uid: string;
}): CustomerUserMessage => {
  return {
    id: uuid(),
    type: MessageType.User,
    sent: new Date(),
    text,
    receivedBy: [],
    seenBy: [],
    uid,
    userType: 'customer',
    sentToDialogflow: false,
    userWebsiteContext: { pageView: PARENT_PAGE_VIEW },
  };
};

export const createCheckAvailabilityUserActionMessage = (
  text: string,
  data: ConversationCheckAvailability,
  pageView: PageView
): UserActionMessage => {
  return {
    type: MessageType.UserAction,
    sent: new Date(),
    text,
    receivedBy: [],
    seenBy: [],
    userAction: {
      type: UserActionType.CheckAvailability,
      timestamp: new Date(),
      data,
    },
    userWebsiteContext: { pageView },
  };
};

export const createFormSubmitUserActionMessage = (
  text: string
): UserActionMessage => {
  return {
    type: MessageType.UserAction,
    sent: new Date(),
    text,
    receivedBy: [],
    seenBy: [],
    userAction: {
      type: UserActionType.SubmitForm,
      timestamp: new Date(),
      data: '',
    },
    userWebsiteContext: { pageView: PARENT_PAGE_VIEW },
  };
};

export const createCheckAvailabilityCarouselMessage = (
  data: CheckAvailabilityCarouselData[],
  text: string
): CheckAvailabilityCarouselMessage => {
  return {
    type: MessageType.Custom,
    receivedBy: [],
    seenBy: [],
    sent: new Date(),
    text,
    customType: 'checkAvailability',
    data,
  };
};

export const createErrorMessage = (text: string): SystemMessage => {
  return {
    sent: new Date(),
    type: MessageType.System,
    text,
    receivedBy: [],
    seenBy: [],
  };
};

export const createSystemMessage = (text: string): SystemMessage => {
  return {
    sent: new Date(),
    type: MessageType.System,
    text,
    receivedBy: [],
    seenBy: [],
  };
};

type V2Props = {
  checkAvailability: ConversationCheckAvailabilityReady;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  conversation:
    | DocumentObject<WebsiteConversation>
    | DocumentObject<Conversation>;
  setIsTyping: (isTyping: boolean) => void;
  text: string;
};
/** IMPORTANT FUNCTION: Handles the following:
 * 1. Toggle Overlay loading screen
 * 2. Fetch Product info & Availability for all selected products
 * 3. Store a new "Carousel" message in Firebase
 * 4. Error Handling
 *
 * This is built on the new logic that Andrew built
 * to handle all Reservation systems via a single flow.
 * Has not been tested for Rez Systems besides Rezdy and XOLA,
 * but those should work as long as the TOMIS backend
 * returns a consistent data format.
 */
export const checkAvailabilityV2 = async ({
  checkAvailability,
  setIsLoading,
  setIsOpen,
  conversation,
  setIsTyping,
  text,
}: V2Props) => {
  addBreadcrumb({
    category: Category.CheckAvailability,
    message: 'Checking Availability (V2)...',
    data: checkAvailability,
  });

  const { isValid } = validateCheckAvailabilityParameters(checkAvailability);

  if (!isValid) {
    /** should probably have an error message of some kind
     * However, this should not be called without validating in advance
     */
    return;
  }

  setIsLoading(true);

  const { dateTimeEnd, dateTimeStart, products, quantity } = checkAvailability;

  /** WWE Chattahoochee (Rezdy) for dev bot, else use firebase config */
  const siteSlug =
    SITE_SLUG === 'tomis-test-site' || SITE_SLUG === 'demo'
      ? 'whitewater-express-chattahoochee'
      : SITE_SLUG;

  /** TOMIS endpoint expects date format YYYY-MM-DD */
  const dateTimeStartString = [
    dateTimeStart.getFullYear(),
    dateTimeStart.getMonth() + 1,
    dateTimeStart.getDate(),
  ].join('-');

  /** TOMIS endpoint expects date format YYYY-MM-DD */
  const dateTimeEndString = [
    dateTimeEnd.getFullYear(),
    dateTimeEnd.getMonth() + 1,
    dateTimeEnd.getDate(),
  ].join('-');

  try {
    const productIds = products.products.map(p => p.productId);

    /** Will not throw an error, should always return the products */
    const productsInfo = await Promise.all(
      productIds.map(async p => await getProductV2(siteSlug, p))
    );

    const {
      error: availabilityError,
      availability: allAvailability,
    } = await getProductAvailabilityV2(
      siteSlug,
      productIds,
      dateTimeStartString,
      dateTimeEndString,
      quantity
    );

    /** Availability Cards that will be shown to the customer.
     * The logic is fairly robust, but an error with
     * formatting in Firebase will likely result in an error
     * here as well. So be careful when editing Firebase!
     */
    const carouselItems: CheckAvailabilityCarouselData[] = productsInfo.map(
      product => {
        /** Availability of this product */
        const availability =
          (allAvailability[product.id] || []).map(
            (a): GetProductAvailability => ({
              bookingUrl: a.booking_url,
              dateTime: a.datetime,
              quantity: a.quantity,
            })
          ) || [];

        /** Display Name stored in Firebase, instead of result from API.
         * We do this b/c API tour name might be really long,
         * longer than we would want the bot to display.
         */
        const friendlyProductName = (
          products.products.find(p => p.productId === product.id) || {
            name: product.name,
          }
        ).name;

        /** Return the CheckAvailabilityCarouselData type */
        return {
          availableTimes: availability,
          currency: product.currency,
          productDescription: product.description,
          productId: product.id,
          productImage: product.image_url,
          productName: friendlyProductName,
          productPrice: product.price,
          error: !product.id || availabilityError, // If product.id is blank, an error occurred fetching product info
        };
      }
    );

    const carouselMessage = createCheckAvailabilityCarouselMessage(
      carouselItems,
      text
    );

    conversation.ref
      .collection(CollectionName.Messages)
      .add(carouselMessage)
      .then(() => {
        setIsLoading(false);
        setIsTyping(false);

        addBreadcrumb({
          category: Category.CheckAvailability,
          message: 'Check Availability completed successfully',
          level: 'info',
        });

        /** collapse check availability menu, but stay keep
         * check availability active=true in firebase for
         * the duration of the conversation
         */
        setIsOpen(false);
      })
      .catch(err => {
        setIsLoading(false);

        logError(err, {
          category: Category.CheckAvailability,
          message: 'Error storing Check Availability carousel message',
          level: 'error',
          type: 'error',
        });
        const firebaseErrorMessage = createErrorMessage(
          'Error checking availability. Please try again later.'
        );
        conversation.ref
          .collection(CollectionName.Messages)
          .add(firebaseErrorMessage);
      });
  } catch (error) {
    setIsLoading(false);

    logError(error, {
      category: Category.CheckAvailability,
      message: 'Error with Check Availability V2',
      level: 'error',
      type: 'error',
    });
    const firebaseErrorMessage = createErrorMessage(
      'Error checking availability. Please try again later.'
    );

    conversation.ref
      .collection(CollectionName.Messages)
      .add(firebaseErrorMessage)
      .catch(error => {
        console.error('error storing error message: ', error);
      });
  }
};
